import * as React from "react";
import { motion } from "motion/react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatMoney, type ShopifyProductNode } from "@/lib/shopify";
import { allPerfumesQueryOptions } from "@/lib/shopify-queries";
import { getProductImageAlt } from "@/lib/image-alt";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";
import { getCurrentChapter, isPurchasable } from "@/config/chapters";
import { STATE_COPY } from "@/config/chapterCopy";


export function Collection({
  initialPerfumes,
}: {
  initialPerfumes: ShopifyProductNode[];
}) {
  // The homepage route loader primes this cache server-side, so the initial
  // render has data and the grid is in the SSR HTML (crawlable, no flicker).
  const { data: perfumes, isLoading } = useQuery({
    ...allPerfumesQueryOptions,
    initialData: initialPerfumes,
  });
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => setHydrated(true), []);

  return (
    <section id="collection" className="relative py-14 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <p className="eyebrow">Featured Fragrances</p>
          <h2 className="mt-4 text-2xl text-parchment sm:mt-6 sm:text-4xl lg:text-5xl">
            The <span className="italic text-gold/90">Current Chapter</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground sm:mt-6 sm:text-xl">
            Finished batches from the season's work, hand-filled in limited
            numbers and released only while they last.
          </p>
          <div className="ornament mt-6 sm:mt-10">
            <span>✶</span>
          </div>
        </div>

        {/* Status text is rendered only after hydration: the server may have
            data the client cache does not yet have (or vice versa), and
            branching on it during the first client render causes a
            hydration mismatch. */}
        {hydrated && (!perfumes || perfumes.length === 0) && (
          <p className="mt-20 text-center text-sm uppercase tracking-[0.22em] text-muted-foreground">
            {isLoading ? "Opening the cabinet…" : "No fragrances published yet."}
          </p>
        )}

        <div className="mx-auto mt-10 flex snap-carousel sm:mt-16 sm:grid sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {(() => {
            const FEATURED_ORDER = [
              "a-gentle-madness",
              "velvet-sugar",
              "the-archivist",
            ];
            const filtered = (perfumes ?? []).filter(
              (p) =>
                p.handle !== "discovery-sets" &&
                !p.handle.startsWith("discovery-set") &&
                p.handle !== "chapter-vials",
            );
            const ranked = [...filtered].sort((a, b) => {
              const ai = FEATURED_ORDER.indexOf(a.handle);
              const bi = FEATURED_ORDER.indexOf(b.handle);
              if (ai === -1 && bi === -1) return 0;
              if (ai === -1) return 1;
              if (bi === -1) return -1;
              return ai - bi;
            });
            return ranked.map((p, i) => {
            const firstVariant = p.variants.edges[0]?.node;
            const price = firstVariant
              ? formatMoney(firstVariant.price.amount, firstVariant.price.currencyCode)
              : null;
            const chapterState = getCurrentChapter().state;
            const soldOut = !isPurchasable(chapterState);
            const gridStatus = STATE_COPY[chapterState].gridStatus;

            const rawNotes = p.accents?.value ?? p.subtitle?.value ?? "";
            let noteList: string[] = [];
            try {
              const parsed = JSON.parse(rawNotes);
              if (Array.isArray(parsed)) noteList = parsed.filter((x) => typeof x === "string");
            } catch {
              noteList = rawNotes
                .split(/\s*(?:•|·|\||,)\s*/)
                .map((s) => s.trim())
                .filter(Boolean);
            }
            const image = p.featuredImage?.url;
            const imageAlt = getProductImageAlt({
              shopifyAlt: p.featuredImage?.altText,
              handle: p.handle,
              title: p.title,
              subtitle: p.subtitle?.value,
            });
            const folio = `Plate · ${String(i + 1).padStart(2, "0")}`;

            return (
              <div key={p.id} className="snap-item">
                <Link
                  to="/products/$slug"
                  params={{ slug: p.handle }}
                  className="contents"
                >
                  <article className="group flex h-full flex-col border border-border bg-background/40 p-6 transition-colors duration-500 hover:border-gold/50">
                    <div className="relative aspect-[4/5] overflow-hidden border border-border/60 bg-card">
                      {image ? (
                        <ResponsiveImage
                          src={image}
                          alt={imageAlt}
                          width={900}
                          height={1200}
                          sizes={SIZES.card}
                          pictureClassName="contents"
                          className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                        />
                      ) : null}
                      <span className="absolute left-3 top-3 bg-background/85 px-2 py-1 font-display text-[10px] uppercase tracking-[0.28em] text-gold/80 backdrop-blur-sm">
                        {folio}
                      </span>
                      {soldOut && (
                        <span className="absolute right-3 top-3 border border-gold/60 bg-background/80 px-2 py-1 font-display text-[10px] uppercase tracking-[0.22em] text-gold backdrop-blur-sm">
                          {gridStatus}
                        </span>
                      )}

                    </div>

                    <h3 className="mt-6 font-display text-xl tracking-[0.1em] text-parchment">
                      {p.title}
                    </h3>
                    {noteList.length > 0 && (
                      <p className="mt-2 font-serif text-sm uppercase tracking-[0.14em] text-gold/80">
                        {noteList.slice(0, -1).join(" · ")}
                        {noteList.length > 1 && " · "}
                        <span className="italic normal-case tracking-[0.04em] text-gold/95">
                          {noteList[noteList.length - 1]}
                        </span>
                      </p>
                    )}
                    <div className="mt-4 h-px w-10 bg-gold/40" />
                    <div className="mt-6 flex items-baseline justify-between gap-4">
                      <span className="font-display text-xs uppercase tracking-[0.24em] text-gold/80 transition-colors group-hover:text-gold">
                        View fragrance →
                      </span>
                      {price && (
                        <span className="font-display text-[11px] uppercase tracking-[0.2em] text-parchment/80">
                          {price}
                        </span>
                      )}
                    </div>
                  </article>
                </Link>
              </div>
            );
          });
          })()}
        </div>


        <div className="mt-20 text-center">
          <Link
            to="/products"
            className="inline-block border-b border-gold/50 pb-1 font-display text-xs uppercase tracking-[0.24em] text-gold transition-colors hover:border-gold hover:text-gold-soft"
          >
            View all perfumes →
          </Link>
        </div>
      </div>
    </section>
  );
}
